"use client";

import { motion, useAnimation, useReducedMotion, Variants } from "framer-motion";
import { forwardRef, useCallback, useImperativeHandle, useRef } from "react";

export interface InfoIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

interface InfoIconProps {
  size?: number;
  duration?: number;
  isAnimated?: boolean;
  isHovered?: boolean;
  className?: string;
}

const InfoIcon = forwardRef<InfoIconHandle, InfoIconProps>(
  (
    {
      className,
      size = 24,
      duration = 1,
      isAnimated = true,
      isHovered = false,
    },
    ref,
  ) => {
    const controls = useAnimation();
    const reduced = useReducedMotion();
    const isControlled = useRef(false);
    const prevHovered = useRef(isHovered);

    useImperativeHandle(ref, () => {
      isControlled.current = true;
      return {
        startAnimation: () =>
          reduced ? controls.start("normal") : controls.start("animate"),
        stopAnimation: () => controls.start("normal"),
      };
    });

    // React to isHovered prop changes
    if (isHovered !== prevHovered.current) {
      prevHovered.current = isHovered;
      if (isHovered && isAnimated && !reduced) {
        controls.start("animate");
      } else if (!isHovered) {
        controls.start("normal");
      }
    }

    const handleEnter = useCallback(() => {
      if (!isAnimated || reduced) return;
      if (!isControlled.current) controls.start("animate");
    }, [controls, reduced, isAnimated]);

    const handleLeave = useCallback(() => {
      if (!isControlled.current) controls.start("normal");
    }, [controls]);

    const svgVariants: Variants = {
      normal: { rotate: 0, scale: 1 },
      animate: {
        rotate: [0, -2, 2, 0],
        scale: [1, 1.08, 0.95, 1],
        transition: { duration: 0.7 * duration, ease: "easeInOut", repeat: 0 },
      },
    };

    const drawVariants: Variants = {
      normal: { pathLength: 1, opacity: 1 },
      animate: {
        pathLength: [0, 1],
        opacity: [0.6, 1],
        transition: { duration: 0.8 * duration, ease: "easeInOut", repeat: 0 },
      },
    };

    const pulseVariants: Variants = {
      normal: { scale: 1, opacity: 1 },
      animate: {
        scale: [1, 1.3, 0.8, 1],
        opacity: [1, 0.5, 1],
        transition: { duration: 0.5 * duration, ease: "easeInOut", repeat: 0 },
      },
    };

    return (
      <motion.div
        className={`inline-flex items-center justify-center ${className || ""}`}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        style={{ flexShrink: 0 }}
      >
        <motion.svg
          xmlns="http://www.w3.org/2000/svg"
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          animate={controls}
          initial="normal"
          variants={svgVariants}
        >
          <motion.circle cx="12" cy="12" r="10" variants={drawVariants} />
          <motion.path d="M12 16v-4" variants={pulseVariants} />
          <motion.path d="M12 8h.01" variants={pulseVariants} />
        </motion.svg>
      </motion.div>
    );
  },
);

InfoIcon.displayName = "InfoIcon";
export { InfoIcon };
